import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchProviders } from "../lib/dataFetchers";
import { cn } from "../lib/cn";

// Backend OAuth-callback redirects rejected sign-ins back to /auth/signin?error=…
// Each known code maps to a friendly, non-leaky message. Unknown non-empty
// values fall back to the generic key. See backend/src/routes/auth.py.
const ERROR_KEYS: Record<string, string> = {
  not_authorized: "error_not_authorized",
  google_failed: "error_google_failed",
  google_noemail: "error_google_noemail",
};

export default function SignIn(): JSX.Element {
  const { t } = useTranslation("auth");
  const [starting, setStarting] = useState(false);
  const [searchParams] = useSearchParams();

  const errorParam = searchParams.get("error");
  const errorKey = errorParam
    ? (ERROR_KEYS[errorParam] ?? "error_generic")
    : null;

  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: fetchProviders,
  });

  // Google OAuth is the only access path. The backend invitation gate decides
  // who is allowed to sign in.
  const isLoading = providers === undefined;
  const googleEnabled = providers?.google === true;

  return (
    <main
      className={cn(
        "relative min-h-screen overflow-hidden",
        "flex items-center justify-center px-4 py-6",
        "safe-top safe-bottom",
      )}
      style={{
        background: "linear-gradient(180deg, #f6f7fb 0%, #f1f2f7 60%, #edeef4 100%)",
      }}
    >
      {/* Soft radial glow — decorative */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[46%] z-0 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-90"
        style={{
          filter: "blur(72px)",
          background: "radial-gradient(closest-side, #e6e9f3, transparent 70%)",
        }}
      />

      {/* Oversized symbol watermark — decorative */}
      <img
        src="/hightekers-symbol.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 -rotate-[8deg] opacity-[.04]"
        style={{ width: "min(108vmin, 860px)" }}
      />

      {/* Card — proto .panel + shadow-pop */}
      <div
        className={cn(
          "relative z-[2] w-full max-w-[374px]",
          "rounded-[11px] border border-border",
          "bg-surface",
          "px-8 py-9 text-center",
          "shadow-pop",
        )}
      >
        {/* Official wordmark */}
        <img
          src="/hightekers-wordmark.svg"
          alt="Hightekers"
          className="mx-auto mb-6 block h-[21px] w-auto"
        />

        <h1
          className="m-0 text-[20px] font-semibold tracking-[-0.018em]"
          style={{ color: "#141c25" }}
        >
          {t("signin_heading")}
        </h1>

        <p className="mt-1.5 mb-6 text-[13.5px] leading-relaxed text-text-muted">
          {t("signin_subtitle")}
        </p>

        {/* ── Render-state region ── */}

        {errorKey && (
          <div
            role="alert"
            aria-live="polite"
            data-testid="signin-error"
            className="mb-5 rounded-[11px] border border-[#f0c9c4] bg-[#fdf3f2] px-3.5 py-2.5 text-left text-[12.5px] leading-relaxed text-[#9a3b30]"
          >
            {t(errorKey)}
          </div>
        )}

        {isLoading && (
          <div aria-busy="true">
            <div className="skeleton h-12 rounded-md" />
          </div>
        )}

        {googleEnabled && (
          <>
            <a
              href="/api/auth/google"
              aria-label={t("google_button")}
              data-testid="google-signin-btn"
              onClick={() => setStarting(true)}
              className={cn(
                "inline-flex h-12 w-full items-center justify-center gap-2.5",
                "rounded-[11px] border bg-white font-semibold text-sm",
                "text-[#141c25] no-underline transition-all",
                "hover:bg-[#fdfdff]",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
              )}
              style={{
                borderColor: "#e1e2e8",
                boxShadow: "0 1px 2px rgba(20,28,37,.04)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "#cdced8";
                (e.currentTarget as HTMLAnchorElement).style.boxShadow =
                  "0 4px 13px rgba(20,28,37,.10)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "#e1e2e8";
                (e.currentTarget as HTMLAnchorElement).style.boxShadow =
                  "0 1px 2px rgba(20,28,37,.04)";
              }}
            >
              {starting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <GoogleG />
              )}
              <span>{starting ? t("google_connecting") : t("google_button")}</span>
            </a>

            <p className="mt-[18px] text-[12.5px] leading-[1.55] text-text-dim">
              {t("access_note")}{" "}
              <a
                href="mailto:muhammed.marvan@hightekers.com?subject=Corporate%20Map%20access"
                className="font-medium text-accent hover:underline"
              >
                {t("google_contact_admin")}
              </a>
            </p>
          </>
        )}

        {!isLoading && !googleEnabled && (
          <div data-testid="google-not-configured">
            <p className="text-sm font-medium text-text">
              {t("google_unconfigured_title")}
            </p>
            <p className="mt-2 text-[12px] leading-relaxed text-text-muted">
              {t("google_unconfigured_body")}
            </p>
          </div>
        )}

        {/* Audit footer */}
        <p className="mt-5 text-[11.5px] text-text-dim">
          {t("footer_audit")}
        </p>
      </div>
    </main>
  );
}

/**
 * Official Google "G" mark — 4-colour SVG, 18×18 display size.
 * Inline so we never load anything from accounts.google.com.
 * Paths from the Google Brand Guidelines (standard G monogram, viewBox 0 0 24 24).
 */
function GoogleG(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {/* Blue */}
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      {/* Green */}
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      {/* Yellow */}
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      />
      {/* Red */}
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
